import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { CHART, PIE_COLORS } from "@/lib/brand";
import { KpiCard } from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Banknote,
  Loader2,
  PieChart as PieIcon,
} from "lucide-react";

function ChartTooltip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover/95 backdrop-blur border border-border rounded-md shadow-xl px-3 py-2 text-xs">
      <p className="font-semibold mb-1 font-heading">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center justify-between gap-4 font-mono">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="text-foreground">{formatMoney(p.value, currency, { compact: true })}</span>
        </p>
      ))}
    </div>
  );
}

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const TICK_STYLE = { fontSize: 11, fill: CHART.axis, fontFamily: "JetBrains Mono" };
const AXIS_LINE_STYLE = { stroke: CHART.grid };
const LEGEND_STYLE = { fontSize: 12, paddingTop: 8 };
const CURSOR_STYLE = { fill: "rgba(255,255,255,0.04)" };
const BAR_RADIUS = [3, 3, 0, 0];
const NET_DOT = { r: 2.5 };

export default function Dashboard() {
  const { currency, rates } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("all");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/dashboard?currency=${currency}&month=${month}`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error("Failed to load dashboard data", e);
      })
      .finally(() => setLoading(false));
  }, [currency, rates, month]);

  if (!data) {
    return (
      <div className="py-32 flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-brand" />
      </div>
    );
  }

  const k = data.kpis;
  const c = data.comparison;
  const isMonth = data.scope === "month";
  const suffix = isMonth ? `(${data.period_label})` : "(consolidated)";
  const trendSub = isMonth ? "vs prev month" : "latest vs prior month";

  return (
    <div className="space-y-7">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">P&amp;L Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.period_label} · all figures in <span className="text-foreground font-medium">{currency}</span>
            {loading && <Loader2 className="inline h-3.5 w-3.5 animate-spin ml-2 text-brand" />}
          </p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-full sm:w-56" data-testid="dashboard-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {data.available_months.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="pnl-revenue-card" label={`Revenue ${suffix}`} value={formatMoney(k.revenue, currency, { compact: true })} icon={TrendingUp} accent="success" trend={c.revenue} sub={trendSub} />
        <KpiCard testId="pnl-expenses-card" label={`Expenses ${suffix}`} value={formatMoney(k.expenses, currency, { compact: true })} icon={TrendingDown} accent="danger" trend={c.expenses} sub={trendSub} />
        <KpiCard testId="pnl-netprofit-card" label={`Net Profit ${suffix}`} value={formatMoney(k.net_profit, currency, { compact: true })} icon={Banknote} accent={k.net_profit >= 0 ? "success" : "danger"} trend={c.net_profit} sub={trendSub} />
        <KpiCard testId="pnl-cash-card" label="Cash in Bank" value={formatMoney(k.cash_balance, currency, { compact: true })} icon={Wallet} accent="info" sub="across all accounts" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Revenue vs Expenses</h3>
              <p className="text-xs text-muted-foreground">Monthly comparison with net profit trend</p>
            </div>
          </div>
          <div className="h-[320px]" data-testid="pnl-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.monthly} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="month" tick={TICK_STYLE} tickLine={false} axisLine={AXIS_LINE_STYLE} />
                <YAxis tick={TICK_STYLE} tickLine={false} axisLine={false}
                  tickFormatter={(v) => formatMoney(v, currency, { compact: true })} width={64} />
                <Tooltip content={<ChartTooltip currency={currency} />} cursor={CURSOR_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey="revenue" name="Revenue" fill={CHART.revenue} radius={BAR_RADIUS} maxBarSize={26} />
                <Bar dataKey="expenses" name="Expenses" fill={CHART.expenses} radius={BAR_RADIUS} maxBarSize={26} />
                <Line dataKey="net_profit" name="Net Profit" stroke={CHART.net} strokeWidth={2.5} dot={NET_DOT} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieIcon className="h-4 w-4 text-brand" />
            <h3 className="font-heading text-lg font-semibold">Expense Mix</h3>
          </div>
          <div className="h-[240px]" data-testid="expense-breakdown-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.expense_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%"
                  innerRadius={52} outerRadius={88} paddingAngle={2} stroke="#030305" strokeWidth={2}>
                  {data.expense_breakdown.map((e, i) => (
                    <Cell key={e.category} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip currency={currency} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {data.expense_breakdown.slice(0, 5).map((e, i) => (
              <div key={e.category} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-foreground/80">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  {e.category}
                </span>
                <span className="font-mono text-muted-foreground">{formatMoney(e.amount, currency, { compact: true })}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Ratios */}
      <div>
        <h3 className="font-heading text-lg font-semibold mb-3">Key Financial Ratios</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {data.ratios.map((r) => (
            <Card key={r.name} className="p-5" data-testid={`ratio-${r.name.replace(/\W+/g, "-").toLowerCase()}`}>
              <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">{r.name}</p>
              <p className="mt-2 font-mono text-2xl font-medium">
                {r.value}
                <span className="text-base text-muted-foreground ml-0.5">{r.suffix}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">{r.hint}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* Balance snapshot */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5">
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">Gross Profit</p>
          <p className="mt-2 font-mono text-xl font-medium">{formatMoney(k.gross_profit, currency, { compact: true })}</p>
        </Card>
        <Card className="p-5">
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">Receivable (AR)</p>
          <p className="mt-2 font-mono text-xl font-medium text-success">{formatMoney(k.total_receivables, currency, { compact: true })}</p>
        </Card>
        <Card className="p-5">
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">Payable (AP)</p>
          <p className="mt-2 font-mono text-xl font-medium text-danger">{formatMoney(k.total_payables, currency, { compact: true })}</p>
        </Card>
        <Card className="p-5">
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">Investments</p>
          <p className="mt-2 font-mono text-xl font-medium">{formatMoney(k.total_investments, currency, { compact: true })}</p>
        </Card>
      </div>
    </div>
  );
}
